<template>
  <vee-form v-slot="{ validate }" as="" :initial-errors="initialErrors">
    <form
      ref="formRef"
      :class="formClasses"
      method="post"
      :action="formAction"
      novalidate
      @submit.prevent="handleSubmit(validate)"
    >
      <text-input
        v-model="formData.login_username"
        input-id="login_username"
        label="Username"
        :show-label="false"
        placeholder="Username"
        rules="required"
        autocomplete="username"
        data-hj-suppress
      />

      <text-input
        v-model="formData.password"
        input-id="password"
        type="password"
        label="Password"
        :show-label="false"
        placeholder="Password"
        rules="required"
        autocomplete="password"
        data-hj-suppress
      />

      <checkbox-input v-model="formData.remember" input-id="remember" label="Remember Me" />

      <!-- additional form inputs -->
      <h-captcha
        :error="captchaError"
        @verify="captchaVerify"
        @expired="captchaExpired"
        @error="captchaExpired"
      />

      <div class="form-actions">
        <csrf-input />

        <div class="form-action -block">
          <submit-button
            :submitting="processing"
            :label="buttonText"
            submitting-label="Processing…"
            classes="-main -block"
            @submit-action="handleSubmit(validate)"
          />
        </div>
        <p class="form-action -block">
          <a
            v-if="forgotPasswordTarget === '_blank'"
            class="btn -anchor-link"
            href="/password/forgot"
            target="_blank"
            rel="noopener noreferrer"
            >Forgot Password?</a
          >
          <a v-else class="btn -anchor-link" href="/password/forgot">Forgot Password?</a>
        </p>
      </div>
    </form>
  </vee-form>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { Form as VeeForm } from 'vee-validate'
import TextInput from '@/components/forms/text-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import HCaptcha from '@/components/forms/hcaptcha.vue'
import { errorBagToInitialErrors } from '@/utils/forms/validation-rules'

/**
 * "blender" auth registration from
 * - if showAside, lots of container classes change to accommodate /join styling
 * - otherwise see organization join, which is much more simple
 *
 * Side effect is empty div container classes
 */
export default defineComponent({
  name: 'LoginForm',
  components: {
    VeeForm,
    TextInput,
    CheckboxInput,
    CsrfInput,
    SubmitButton,
    HCaptcha,
  },
  props: {
    errorBag: {
      type: Object,
      default: () => ({}),
    },
    initFormData: {
      type: Object,
      default: () => ({}),
    },

    // defaults from blade to vue migration
    showTosInput: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: 'Sign In',
    },
    formAction: {
      type: String,
      default: '/join',
    },
    formClasses: {
      type: String,
      default: 'standard-form',
    },
    forgotPasswordTarget: {
      type: String,
      default: '_self',
    },
  },
  data() {
    return {
      processing: false,
      captchaError: false,
      formData: {
        login_username: '',
        password: '',
        remember: true,
        captcha: '',

        // allow original form data
        ...this.initFormData,
      },
    }
  },

  computed: {
    // only return first error per field, backend returns array
    initialErrors(): Record<string, string> {
      return errorBagToInitialErrors(this.errorBag)
    },
  },

  // instance methods
  methods: {
    captchaVerify(response: string) {
      this.formData.captcha = response
      this.captchaError = false
    },

    captchaExpired() {
      this.formData.captcha = ''
      this.captchaError = true
    },

    async handleSubmit(validate: Function) {
      if (this.processing) {
        return
      }

      // require captcha to be set
      this.captchaError = false

      if (!this.formData.captcha) {
        this.captchaError = true
        return
      }
      // end captcha

      // disable submit
      this.processing = true

      try {
        const results = await validate()

        // invalid, stop
        if (!results.valid) {
          this.processing = false
          return
        }

        // traditional form submit
        if (this.$refs.formRef instanceof HTMLFormElement) {
          // wait until next tick to make sure v-model update
          await this.$nextTick()
          this.$refs.formRef.submit()
        }
      } catch (e) {
        // if error thrown, enable form
        this.processing = false
      }
    },
  },
})
</script>
